import React, { useCallback, useMemo, useState } from "react";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import useIsDesktop from "../accountShell/useIsDesktop";
import { buildAssetsNav, resolveActiveAssetsNavId, ASSETS_MAIN_SECTION_KEYS } from "../config/assetsNav";
import { AccountHeaderActionsContext } from "../accountShell/accountHeaderActions";
import styles from "../accountShell/index.module.less";

const { useTranslation } = Hooks;

function tr(t, key, fallback) {
  if (!t || !key) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

function RailLink({ item, active }) {
  const Icon = item.icon;
  const className = cx(styles.railLink, active && styles.railLinkActive);

  return (
    <a href={item.href} className={className}>
      <Icon className={cx(styles.railIcon, active && styles.railIconActive)} />
      <span>{item.label}</span>
    </a>
  );
}

export function AssetsPageHeader({ tabKey, actions, onBack }) {
  const t = useTranslation();
  const meta = ASSETS_MAIN_SECTION_KEYS[tabKey];
  if (!meta) return null;

  return (
    <header className={cx(styles.pageHeader, (actions || onBack) && styles.pageHeaderWithActions)}>
      <div className={styles.pageHeaderText}>
        {onBack ? (
          <>
            <button
              type="button"
              className={styles.pageHeaderBack}
              onClick={onBack}
              aria-label={tr(t, "activity.back", "Back")}
            >
              <svg className={styles.pageHeaderBackIcon} width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
                <path
                  d="M8 2L2 8L8 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{tr(t, "activity.back", "Back")}</span>
            </button>
            <h1 className={styles.pageTitle}>{tr(t, meta.labelKey, meta.labelFallback)}</h1>
            {meta.subtitleKey ? (
              <p className={styles.pageSubtitle}>{tr(t, meta.subtitleKey, meta.subtitleFallback)}</p>
            ) : null}
          </>
        ) : (
          <>
            <h1 className={styles.pageTitle}>{tr(t, meta.labelKey, meta.labelFallback)}</h1>
            {meta.subtitleKey ? (
              <p className={styles.pageSubtitle}>{tr(t, meta.subtitleKey, meta.subtitleFallback)}</p>
            ) : null}
          </>
        )}
      </div>
      {actions ? <div className={styles.pageHeaderActions}>{actions}</div> : null}
    </header>
  );
}

export default function AssetsShell({ tabKey, children, onBack }) {
  const t = useTranslation();
  const { isDesktop, isReady } = useIsDesktop();
  const [headerActions, setHeaderActionsState] = useState(null);
  const setHeaderActions = useCallback((node) => {
    setHeaderActionsState(node);
  }, []);
  const headerActionsCtx = useMemo(() => ({ setHeaderActions }), [setHeaderActions]);

  const navItems = useMemo(() => {
    if (!isReady || !isDesktop) return [];
    return buildAssetsNav({ t, getUrl: Util.getUrl });
  }, [t, isReady, isDesktop]);
  const activeId = resolveActiveAssetsNavId(navItems, tabKey);

  if (!isReady || !isDesktop) {
    return (
      <AccountHeaderActionsContext.Provider value={headerActionsCtx}>
        {children}
      </AccountHeaderActionsContext.Provider>
    );
  }

  return (
    <AccountHeaderActionsContext.Provider value={headerActionsCtx}>
      <div className={styles.shell}>
        <div className={styles.inner}>
          <aside className={styles.aside}>
            <div className={styles.asideSticky}>
              <div className={styles.railSection}>
                <p className={styles.sectionTitle}>{tr(t, "nav.assets", "Assets")}</p>
                <nav className={styles.rail}>
                  {navItems.map((item) => (
                    <RailLink key={item.id} item={item} active={item.id === activeId} />
                  ))}
                </nav>
              </div>
            </div>
          </aside>
          <main className={styles.main}>
            <AssetsPageHeader tabKey={tabKey} actions={headerActions} onBack={onBack} />
            {children}
          </main>
        </div>
      </div>
    </AccountHeaderActionsContext.Provider>
  );
}
